import {apiClient, queryClient} from '@common/http/query-client';
import {track, toValue} from '@common/analytics/track';
import {useMutation, useQuery} from '@tanstack/react-query';
import {showHttpErrorToast} from '@common/http/show-http-error-toast';
import {create} from 'zustand';
import {auth} from '@common/auth/use-auth';
import {rememberReturnPath} from '@common/auth/return-path';
import {toast} from '@ui/toast/toast';
import {message} from '@ui/i18n/message';

/**
 * The guest cart's token.
 *
 * A guest's cart lives on the server under a random token, and the server can
 * only find it again if the browser sends that token back. Nothing did, so
 * every guest request started a new empty cart: the item appeared once, then
 * the university picker 404'd and checkout showed nothing to pay for.
 *
 * Kept in localStorage so it survives a refresh and the trip through login,
 * where the server folds it into the account's cart. Cleared once a response
 * carries no token — that is a signed-in cart, and the guest one is gone.
 */
const CART_TOKEN_KEY = 'masertak.cart_token';
const CART_URL = /(^|\/)(cart|checkout)(\/|\?|$)/;

function readCartToken(): string | null {
  try {
    return localStorage.getItem(CART_TOKEN_KEY);
  } catch {
    return null;
  }
}

const w = window as any;
if (!w.__cartTokenInterceptors) {
  w.__cartTokenInterceptors = true;
  apiClient.interceptors.request.use(config => {
    const token = CART_URL.test(config.url ?? '') ? readCartToken() : null;
    if (token) {
      config.headers = config.headers ?? {};
      (config.headers as any)['X-Cart-Token'] = token;
    }
    return config;
  });
  apiClient.interceptors.response.use(response => {
    const cart = response?.data?.cart;
    if (cart && typeof cart === 'object' && 'session_token' in cart) {
      try {
        if (cart.session_token) {
          localStorage.setItem(CART_TOKEN_KEY, cart.session_token);
        } else if (cart.id) {
          // A saved cart without a token belongs to an account.
          localStorage.removeItem(CART_TOKEN_KEY);
        }
      } catch {
        // Storage blocked: the cart still works for this page view.
      }
    }
    return response;
  });
}

export interface CartItem {
  id: number;
  item_type: 'service' | 'package';
  item_id: number;
  name: string;
  price: number;        // cents
  quantity: number;
  scholarship_id: number | null;
  service_link_id: number | null;
  package_link_id: number | null;
  currency: string;
  /** The opportunity this line is for. Every line has one — that is the rule. */
  listing_title: string | null;
  /** Optional extras already priced into this line. A negative price is a deduction. */
  addons: {label: string; price: number}[];
  /** The quantity band, when the service is sold in bands. */
  tier_id: number | null;
  /** Ranked choices: university id (`u`) and the department's program_link id (`p`). */
  preferences: {u: number; p: number | null}[];
  /** How many the band pays for. 0 when the line has no band. */
  preferences_needed: number;
  /** Set when this package allows a deposit. */
  deposit_percentage: number | null;
  /** Whether the student chose to pay the deposit now. */
  pay_deposit: boolean;
  /** Cents due on the first invoice, and on the second. */
  due_now: number;
  due_later: number;
}

export interface Cart {
  id: number;
  session_token: string | null;
  /** Anything the buyer wants the team to know. */
  notes: string | null;
  items: CartItem[];
  /** Cents due on the first invoice, and on the balance invoice. */
  due_now: number;
  due_later: number;
  coupon_code: string | null;
  subtotal: number;     // cents
  discount: number;     // cents
  /** The partner discount, separate from a coupon. */
  referral_discount?: number;
  referral?: {
    eligible: boolean;
    discount_cents: number;
    partner_id: number | null;
    partner_name: string | null;
    message: string | null;
    reason: string | null;
    preview?: boolean;
    /** The student took it off this cart (DELETE cart/referral). */
    removed?: boolean;
  } | null;
  total: number;        // cents
  currency: string;
  /** Enabled payment methods (same as the invoice "Pay now"). */
  payment_methods?: {key: string; label: string; instant: boolean}[];
}

// Drawer open/close UI state (server holds the items).
interface CartUi {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}
export const useCartUi = create<CartUi>(set => ({
  isOpen: false,
  open: () => set({isOpen: true}),
  close: () => set({isOpen: false}),
  toggle: () => set(s => ({isOpen: !s.isOpen})),
}));

export function useCart() {
  return useQuery({
    queryKey: ['cart'],
    queryFn: () => apiClient.get<{cart: Cart}>('cart').then(r => r.data.cart),
    staleTime: 10_000,
  });
}

function invalidateCart() {
  queryClient.invalidateQueries({queryKey: ['cart']});
}

/** Thrown instead of posting when a guest presses "Add to cart". */
class SignInRequired extends Error {}

/**
 * Only signed-in students can add to the cart. A guest is sent to sign in and
 * brought back to the page they were on (the server refuses guests too).
 */
export function useAddToCart() {
  return useMutation({
    mutationFn: (payload: {item_type: 'service' | 'package'; item_id: number; scholarship_id?: number; service_link_id?: number; package_link_id?: number; quantity?: number; addon_ids?: number[]; tier_id?: number; preferences?: number[]; cv_document_id?: number}) => {
      if (!auth.user) {
        sendGuestToLogin();
        return Promise.reject(new SignInRequired());
      }
      return apiClient.post<{cart: Cart}>('cart/items', payload).then(r => r.data.cart);
    },
    onSuccess: (cart, payload) => {
      queryClient.setQueryData(['cart'], cart);
      useCartUi.getState().open();
      const line = cart.items.find(i => i.item_type === payload.item_type && i.item_id === payload.item_id);
      track('add_to_cart', {
        currency: 'USD',
        value: toValue(line ? line.price * Math.max(line.quantity, 1) : 0),
        items: [{item_id: `${payload.item_type}-${payload.item_id}`, item_name: line?.name, price: toValue(line?.price), quantity: line?.quantity ?? 1}],
      });
    },
    onError: err => {
      if (err instanceof SignInRequired) return;
      if ((err as any)?.response?.status === 401) {
        sendGuestToLogin();
        return;
      }
      showHttpErrorToast(err);
    },
  });
}

function sendGuestToLogin() {
  const here = window.location.pathname + window.location.search;
  rememberReturnPath(here);
  toast(message('Sign in to add this to your cart'));
  const to = `/login?redirect=${encodeURIComponent(here)}`;
  // Through the app's router so the toast survives; the dynamic import avoids
  // an import cycle (the router's tree imports this file).
  import('@app/app-router')
    .then(m => m.appRouter.navigate(to))
    .catch(() => window.location.assign(to));
}

export function useUpdateCartItem() {
  return useMutation({
    mutationFn: ({itemId, quantity}: {itemId: number; quantity: number}) =>
      apiClient.put<{cart: Cart}>(`cart/items/${itemId}`, {quantity}).then(r => r.data.cart),
    onSuccess: cart => queryClient.setQueryData(['cart'], cart),
    onError: err => showHttpErrorToast(err),
  });
}

export function useRemoveCartItem() {
  return useMutation({
    mutationFn: (itemId: number) =>
      apiClient.delete<{cart: Cart}>(`cart/items/${itemId}`).then(r => r.data.cart),
    onSuccess: cart => queryClient.setQueryData(['cart'], cart),
    onError: err => showHttpErrorToast(err),
  });
}

/** Set which universities/programmes a tiered line is for. */
export function useSavePreferences() {
  return useMutation({
    mutationFn: ({itemId, preferences}: {itemId: number; preferences: {u: number; p: number | null}[]}) =>
      apiClient
        .put<{cart: Cart}>(`cart/items/${itemId}/preferences`, {preferences})
        .then(r => r.data.cart),
    onSuccess: cart => queryClient.setQueryData(['cart'], cart),
    onError: err => showHttpErrorToast(err),
  });
}

/** Pay a package's deposit now, or pay it in full. */
export function useSaveDepositChoice() {
  return useMutation({
    mutationFn: ({itemId, payDeposit}: {itemId: number; payDeposit: boolean}) =>
      apiClient
        .put<{cart: Cart}>(`cart/items/${itemId}/deposit`, {pay_deposit: payDeposit})
        .then(r => r.data.cart),
    onSuccess: cart => queryClient.setQueryData(['cart'], cart),
    onError: err => showHttpErrorToast(err),
  });
}

/** Save the buyer's note on the cart. */
export function useSaveCartNotes() {
  return useMutation({
    mutationFn: (notes: string) =>
      apiClient.put<{cart: Cart}>('cart/notes', {notes}).then(r => r.data.cart),
    onSuccess: cart => queryClient.setQueryData(['cart'], cart),
    onError: err => showHttpErrorToast(err),
  });
}

export function useApplyCoupon() {
  return useMutation({
    mutationFn: (code: string) =>
      apiClient.post<{cart: Cart}>('cart/coupon', {code}).then(r => r.data.cart),
    onSuccess: cart => queryClient.setQueryData(['cart'], cart),
    onError: err => showHttpErrorToast(err),
  });
}

export function useRemoveCoupon() {
  return useMutation({
    mutationFn: () => apiClient.delete<{cart: Cart}>('cart/coupon').then(r => r.data.cart),
    onSuccess: cart => queryClient.setQueryData(['cart'], cart),
    onError: () => invalidateCart(),
  });
}

export function useCheckout() {
  return useMutation({
    mutationFn: (payload: {payment_method: string}) =>
      apiClient.post<{order: any; pay_url: string | null; crm_invoice_id?: number | null}>('checkout', payload).then(r => r.data),
    onSuccess: () => invalidateCart(),
    onError: err => showHttpErrorToast(err),
  });
}
